import { useEffect, useRef, useState } from "react";
import {
  type FieldError,
  type FieldErrors,
  type FieldValues,
  type Path,
  type UseFormProps,
  type UseFormReturn,
  useForm,
  useFormContext as useFormContextForm,
} from "react-hook-form";

import { type ValidationError } from "../types";
import { useMergeErrors } from "./useMergeErrors";

type Strictify<T> = { [K in keyof T]-?: T[K] & Exclude<T[K], undefined> };

export type UseFormErrorReturn<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TDeps extends [any, ...any] = any,
> = Omit<UseFormReturn<TFieldValues, TContext, TDeps>, "register"> & {
  _register: UseFormReturn<TFieldValues, TContext, TDeps>["register"];
  register: UseFormReturn<TFieldValues, TContext, TDeps>["register"] & {
    error?: FieldError;
  };
};

export function useFormError<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TDeps extends [any, ...any] = any,
>(
  {
    errors,
    ...props
  }: Omit<UseFormProps<TFieldValues, TContext>, "errors"> & {
    errors?: ValidationError;
  },
  defaultValue?: (...deps: Strictify<TDeps>) => TFieldValues,
  deps?: TDeps,
): UseFormErrorReturn<TFieldValues, TContext, TDeps> &
  ReturnType<typeof useMergeErrors> {
  const resolve = useRef<(value: Strictify<TDeps>) => void>(undefined);
  const [promise] = useState(
    () =>
      new Promise<Strictify<TDeps>>((_resolve) => {
        // prevent overriding in strict mode
        if (!resolve.current) resolve.current = _resolve;
      }),
  );

  useEffect(() => {
    if (deps && !deps.includes(undefined)) {
      resolve.current?.(deps as Strictify<TDeps>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps || []);

  const { register, ...formData } = useForm(
    defaultValue
      ? {
          ...props,
          defaultValues: async () => {
            const deps = await promise;

            return defaultValue(...deps);
          },
        }
      : props,
  );

  const registerWithError: (
    name: Parameters<typeof register>[0],
    options?: Parameters<typeof register>[1],
  ) => ReturnType<typeof register> & {
    error?: string;
  } = (name, options) => {
    const error = getDescendantProp(
      formData.formState.errors,
      name,
    ) as FieldError;

    return error
      ? {
          ...register(name, options),
          error: error.message,
        }
      : register(name, options);
  };

  const { error } = useMergeErrors(formData.formState.errors, errors);

  return {
    _register: register,
    register: registerWithError,
    ...formData,
    error,
  } as any;
}

function getDescendantProp<TFieldValues extends FieldValues>(
  obj: FieldErrors<TFieldValues>,
  desc: Path<TFieldValues>,
): FieldError {
  let objCopy = obj as any;
  const arr = desc.split(".");
  while (arr.length && (objCopy = obj[arr.shift()!]));
  return objCopy;
}

export function useFormContext<
  TFieldValues extends FieldValues,
  TContext = any,
  TransformedValues extends FieldValues | undefined = undefined,
>() {
  return useFormContextForm() as UseFormReturn<
    TFieldValues,
    TContext,
    TransformedValues
  > &
    ReturnType<typeof useMergeErrors>;
}
